import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';
import { toast } from 'react-toastify';
import { apiBasePath, apiName } from 'assets';
import { ProfileQuestionAnswer } from 'types/ProfileQuestions';
import { AxiosError } from 'axios';
import { useState } from 'react';

const postQuestionAnswers = async (
  answers: ProfileQuestionAnswer[],
  tinyAccountId: string,
  kitId: string,
  isEditMode?: boolean,
  confirmDelete?: boolean,
) => {
  if (!tinyAccountId || !kitId) throw new Error('No info provided');
  if (!answers?.length) throw new Error('No answer provided');
  return API.post(
    apiName,
    `${apiBasePath}/tinyaccounts/${tinyAccountId}/profile-answers?is_sample=1&kit_id=${kitId}${confirmDelete ? '&confirmDelete=1' : ''}${isEditMode ? '&isEditMode=1' : ''}`,
    {
      body: answers,
    },
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.KITS,
        domain: DOMAINS.SURVEYS,
        importance: IMPORTANCE.HIGH,
      },
      context: {
        tinyAccountId,
        kitId,
        answerCount: answers.length,
        isEditMode: !!isEditMode,
        confirmDelete: !!confirmDelete,
      },
    },
  );
};

type UpdateSampleInfoAnswerMutation = {
  answers: ProfileQuestionAnswer[];
  tinyAccountId: string;
  kitId: string;
  isEditMode?: boolean;
  confirmDelete?: boolean;
};

type UpdateSampleInfoAnswerHookResult = ReturnType<
  typeof useMutation<void, unknown, UpdateSampleInfoAnswerMutation>
> & {
  modalProps: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    description: string;
  };
};

const useUpdateSampleInfoAnswer = (): UpdateSampleInfoAnswerHookResult => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingAnswers, setPendingAnswers] = useState<{
    answers: ProfileQuestionAnswer[];
    tinyAccountId: string;
    kitId: string;
  } | null>(null);
  const sampleInfoAnswerMutation = useMutation<
    void,
    unknown,
    UpdateSampleInfoAnswerMutation
  >({
    mutationFn: (params: UpdateSampleInfoAnswerMutation) => {
      return postQuestionAnswers(
        params.answers,
        params.tinyAccountId,
        params.kitId,
        params.isEditMode,
        params.confirmDelete,
      );
    },
    onError: (error: unknown, variables) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          setModalMessage(error.response.data.message);
          setModalOpen(true);
          setPendingAnswers({
            answers: variables.answers,
            tinyAccountId: variables.tinyAccountId,
            kitId: variables.kitId,
          });
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('An error occurred while saving your answers');
        }
      } else {
        toast.error('An error occurred while saving your answers');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('sampleInfoQuestions'),
      });
      queryClient.invalidateQueries({
        predicate: query => query.queryKey.includes('profileSurveys'),
      });
    },
  });
  const handleConfirmDelete = async () => {
    if (pendingAnswers) {
      try {
        await sampleInfoAnswerMutation.mutateAsync({
          ...pendingAnswers,
          isEditMode: true,
          confirmDelete: true,
        });
        setModalOpen(false);
        setPendingAnswers(null);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error('An error occurred while saving your answers');
        }
      }
    }
  };

  return {
    ...sampleInfoAnswerMutation,
    modalProps: {
      isOpen: modalOpen,
      onClose: () => {
        setModalOpen(false);
        setPendingAnswers(null);
      },
      onConfirm: handleConfirmDelete,
      title: 'Confirm Changes',
      description: modalMessage,
    },
  };
};

export default useUpdateSampleInfoAnswer;
